import { ValidationIcon } from "@ui/components/internal/ValidationIcon";
import {
  useWizardFieldMessage,
  WizardFieldFooter,
  type WizardFieldVariants,
  wizardField,
} from "@ui/components/internal/WizardField";
import { resolveValidationState } from "@ui/components/shared/resolveValidationState";
import { tv } from "@ui/lib/tv";
import {
  DateField,
  DateFieldInput,
  DateFieldLabel,
  type DateFieldProps,
  DateFieldSegment,
} from "@ui/primitives/DateField";
import { composeRenderProps, type DateValue } from "@ui/primitives/shared";
import type { ReactNode } from "react";

// The only wizard-date-specific styling: the editable segments. (The pill, rings and footer are
// the shared wizard field chrome.) Distinct from StandardDateField's `dateSegmentClasses` — the
// wizard palette runs a shade darker (neutral-500 placeholders/literals) and the focused-segment
// selection stays neutral rather than blue: the field-level colour ring already carries the
// wizard colour, so a coloured segment highlight would double up.
const dateSegment = tv({
  base: [
    "rounded px-0.5 tabular-nums caret-transparent outline-hidden",
    "data-[placeholder]:text-neutral-500",
    "data-[type=literal]:px-0 data-[type=literal]:text-neutral-500",
    "data-[focused]:bg-neutral-200 data-[focused]:text-neutral-900",
    "data-[focused]:data-[placeholder]:text-neutral-900",
    "data-[disabled]:text-neutral-400",
  ],
  variants: {
    size: {
      xl: "text-lg/6",
      "3xl": "text-xl/6",
      "4xl": "text-xl/6",
      "5xl": "text-xl/6",
    },
  },
  defaultVariants: { size: "5xl" },
});

export interface WizardDateFieldProps<T extends DateValue> extends DateFieldProps<T> {
  label: string;
  isLabelHidden?: boolean;
  color?: WizardFieldVariants["color"];
  size?: WizardFieldVariants["size"];
  /** Leading icon — use sticker icons (`<Icon name="..." type="sticker" />`) to match the wizard
   *  date field design. */
  leadIcon?: ReactNode;
  /** Validation message rendered below the field when `isInvalid`. */
  errorMessage?: ReactNode;
  /** Success message rendered below the field once it has validated clean. Takes a green ring +
   *  check icon. Suppressed while `isInvalid`. Mirrors WizardTextField. */
  successMessage?: ReactNode;
}

export function WizardDateField<T extends DateValue>({
  label,
  isLabelHidden,
  color = "purple",
  size = "5xl",
  leadIcon,
  errorMessage,
  successMessage,
  ...props
}: WizardDateFieldProps<T>) {
  const validationState = resolveValidationState(props.isInvalid, successMessage);
  const { messageId, ariaDescribedBy } = useWizardFieldMessage({
    isInvalid: props.isInvalid,
    errorMessage,
    successMessage,
    ariaDescribedBy: props["aria-describedby"],
  });
  // The colour ring is applied with `focusWithin` (a CSS focus-within rule) rather than the
  // isFocused render-prop path because react-aria's DateField has no focus-state child fn like
  // TextFieldGroup does. `!validationState` keeps the validation ring owning the shadow.
  const styles = wizardField({
    color,
    size,
    validationState,
    isDisabled: props.isDisabled,
    focusWithin: !validationState,
  });
  const segmentClassName = dateSegment({ size });

  return (
    <DateField
      {...props}
      aria-describedby={ariaDescribedBy}
      className={composeRenderProps(props.className, (className) => styles.root({ className }))}
    >
      <DateFieldLabel className={isLabelHidden ? "sr-only" : undefined}>{label}</DateFieldLabel>
      <div className={styles.field()}>
        {leadIcon ? (
          <span aria-hidden="true" className={styles.leadIcon()}>
            {leadIcon}
          </span>
        ) : null}
        {/* Segment row. Each segment is keyboard-editable; the row flexes so the lead/validation
            icons sit flush at the ends and the segments fill the middle. */}
        <DateFieldInput className={styles.input({ className: "flex items-center" })}>
          {(segment) => <DateFieldSegment className={segmentClassName} segment={segment} />}
        </DateFieldInput>
        {validationState ? <ValidationIcon className={styles.validationIcon()} state={validationState} /> : null}
      </div>
      <WizardFieldFooter
        classNames={{ messageSlot: styles.messageSlot(), message: styles.message() }}
        errorMessage={errorMessage}
        isInvalid={props.isInvalid}
        messageId={messageId}
        successMessage={successMessage}
      />
    </DateField>
  );
}
